import { useEffect, useState } from 'react';
import axios from '../utils/axiosInstance';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Receipt, Download, Settings2 } from 'lucide-react';
import { usePlan } from '@/context/PlanContext';

interface BillingDetails {
  plan: string;
  effectivePlan: string;
  planPrice: number; // in paise
  billingCycle: string;
  nextRenewalDate: string | null;
  status: string;
  isInGracePeriod: boolean;
  gracePeriodDaysRemaining: number;
  subscriptionId: string | null;
  subscriptionStatus: string | null;
  subscriptionCurrentEnd: string | null;
  cancelAtCycleEnd: boolean;
  pendingChange: {
    changeType: string;
    sourcePlan: string;
    targetPlan: string;
    status: string;
    pendingChangeAt: string | null;
    currentPeriodEnd: string;
  } | null;
}

interface BillingHistoryItem {
  _id: string;
  razorpay_payment_id: string;
  razorpay_order_id: string;
  amount: number; // in paise
  currency: string;
  status: string;
  plan_at_time: string;
  invoice_url: string | null;
  createdAt: string;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  paymentMethod?: string | null;
}

function formatPaise(paise: number): string {
  return `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusBadge(status: string) {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
    case 'grace':
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Grace Period</Badge>;
    case 'expired':
      return <Badge variant="destructive">Expired</Badge>;
    case 'unpaid':
    case 'free':
      return <Badge variant="secondary">Unpaid</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function paymentStatusBadge(status: string) {
  if (status === 'success') {
    return <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Success</span>;
  }
  return <span className="inline-flex items-center text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">Failed</span>;
}

export default function BillingPage() {
  const [details, setDetails] = useState<BillingDetails | null>(null);
  const [history, setHistory] = useState<BillingHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { openUpgradeModal, planInfo } = usePlan();
  const normalizedPlan = details?.plan === 'paid'
    ? 'pro_max'
    : details?.plan === 'free' ? 'unpaid' : details?.plan;
  const normalizedEffectivePlan = details?.effectivePlan === 'paid'
    ? 'pro_max'
    : details?.effectivePlan === 'free' ? 'unpaid' : details?.effectivePlan;

  const handleDownloadInvoice = async (item: BillingHistoryItem) => {
    if (!item.invoiceId) return;
    setDownloadingId(item._id);
    try {
      const response = await axios.get(`/api/billing/invoice/${item.invoiceId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${item.invoiceNumber || 'invoice'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Silently fail — user can retry
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch billing data when planInfo changes. PlanContext refreshes
  // planInfo on the 'plan:update' socket event (emitted by the backend after
  // payment verification), so this gives us an automatic refresh without a
  // page reload — the user sees the new receipt immediately after payment.
  // Skip the initial fetch (planInfo is null on first render, handled above).
  const planKey = planInfo
    ? `${planInfo.plan}-${planInfo.planExpiresAt}-${planInfo.subscriptionStatus}-${planInfo.cancelAtCycleEnd}`
    : '';
  useEffect(() => {
    if (!loading) {
      fetchData();
    }
  }, [planKey]);

  const fetchData = async () => {
    try {
      const [detailsRes, historyRes] = await Promise.all([
        axios.get('/api/billing/details'),
        axios.get('/api/billing/history'),
      ]);
      setDetails(detailsRes.data);
      setHistory(historyRes.data);
    } catch {
      // Errors handled by empty state
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLoader label="Loading billing..." />
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="page-title">Billing</h2>
      </div>

      <div className="space-y-6 max-w-3xl">

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard size={18} />
            Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          {details ? (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg sm:text-xl font-bold capitalize">{normalizedPlan?.replace('_', ' ')} Plan</span>
                    {statusBadge(details.status)}
                  </div>
                  {normalizedPlan !== 'unpaid' && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatPaise(details.planPrice)} / {details.billingCycle}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => openUpgradeModal(normalizedEffectivePlan === 'unpaid' ? '' : 'manage-plan')}
                  className="gap-2"
                >
                  <Settings2 size={16} />
                  Manage Plan
                </Button>
              </div>

              {normalizedPlan !== 'unpaid' && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                      {details.billingCycle === 'monthly' && !details.cancelAtCycleEnd ? 'Next Renewal' : 'Access Through'}
                    </p>
                    <p className="text-sm font-medium mt-0.5">{formatDate(details.nextRenewalDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Billing Cycle</p>
                    <p className="text-sm font-medium mt-0.5 capitalize">{details.billingCycle}</p>
                  </div>
                </div>
              )}

              {details.pendingChange && details.billingCycle !== 'monthly' && (
                <div className="border-l-2 border-primary pl-3">
                  <p className="text-sm font-medium capitalize">
                    {details.pendingChange.targetPlan.replace('_', ' ')} change pending
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {details.pendingChange.changeType === 'annual_to_monthly'
                      ? 'The ₹599 Pro Max Monthly subscription is being confirmed. Annual Pro remains active until confirmation.'
                      : 'Razorpay confirmation is still pending. Your current entitlement remains protected.'}
                  </p>
                </div>
              )}

              {details.billingCycle === 'monthly' && details.subscriptionId && (
                <div className="space-y-3 border-t border-border pt-4">
                  {details.pendingChange && (
                    <div className="border-l-2 border-primary pl-3">
                      <p className="text-sm font-medium capitalize">
                        {details.pendingChange.targetPlan.replace('_', ' ')} change pending
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {details.pendingChange.changeType === 'monthly_to_annual'
                          ? details.pendingChange.status === 'payment_confirmed'
                            ? 'Annual payment received. Monthly renewal cancellation is being confirmed.'
                            : details.pendingChange.targetPlan === 'pro'
                              ? `Annual Pro starts on ${formatDate(details.pendingChange.pendingChangeAt || details.pendingChange.currentPeriodEnd)}. You can still upgrade the scheduled plan to Annual Pro Max in Manage Plan.`
                              : `Annual Pro Max starts on ${formatDate(details.pendingChange.pendingChangeAt || details.pendingChange.currentPeriodEnd)} after this monthly period ends.`
                          : ['scheduled_downgrade', 'monthly_downgrade_replacement'].includes(details.pendingChange.changeType)
                          ? `Takes effect at renewal on ${formatDate(details.pendingChange.pendingChangeAt || details.pendingChange.currentPeriodEnd)}.`
                          : details.pendingChange.changeType === 'monthly_upgrade_replacement'
                            ? details.pendingChange.status === 'compensation_pending'
                              ? 'The failed upgrade is being refunded. Your Pro subscription remains active.'
                              : 'Your ₹599 payment and Pro Max subscription are being confirmed. Pro remains active until the handoff completes.'
                          : details.pendingChange.changeType === 'renewal_reactivation'
                            ? 'A previous renewal update is still being reconciled.'
                            : 'Razorpay confirmation is still pending. Your current entitlement remains protected.'}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium">Monthly AutoPay</p>
                    <p className="text-xs text-muted-foreground">
                      {details.cancelAtCycleEnd
                        ? `Renewal cancelled. Access remains active through ${formatDate(details.subscriptionCurrentEnd || details.nextRenewalDate)}.`
                        : `Subscription status: ${details.subscriptionStatus || 'processing'}`}
                    </p>
                  </div>
                </div>
              )}

              {details.isInGracePeriod && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 flex flex-col sm:flex-row sm:items-center gap-2">
                  Your plan has expired. You have {details.gracePeriodDaysRemaining} day{details.gracePeriodDaysRemaining !== 1 ? 's' : ''} remaining in the grace period.
                  <Button size="sm" variant="outline" onClick={() => openUpgradeModal()} className="shrink-0">
                    Renew Now
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Unable to load billing details.</p>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt size={18} />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No payments yet.</p>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-sm min-w-160">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Receipt No</th>
                    <th className="pb-2 font-medium text-muted-foreground">Date</th>
                    <th className="pb-2 font-medium text-muted-foreground">Amount</th>
                    <th className="pb-2 font-medium text-muted-foreground">Payment Method</th>
                    <th className="pb-2 font-medium text-muted-foreground">Status</th>
                    <th className="pb-2 font-medium text-muted-foreground">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {history.map(item => (
                    <tr key={item._id}>
                      <td className="py-3 font-medium">{item.invoiceNumber || '—'}</td>
                      <td className="py-3">{formatDate(item.createdAt)}</td>
                      <td className="py-3 font-medium">{formatPaise(item.amount)}</td>
                      <td className="py-3">{item.paymentMethod || '—'}</td>
                      <td className="py-3">{paymentStatusBadge(item.status)}</td>
                      <td className="py-3">
                        {item.invoiceId ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 h-8 px-2"
                            onClick={() => handleDownloadInvoice(item)}
                            disabled={downloadingId === item._id}
                          >
                            <Download size={14} />
                            {downloadingId === item._id ? '...' : 'PDF'}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
